// BL-44 — the audit log in words an operator reads, not a developer.
//
// The log itself is untouched and untouchable: `audit_log` is append-only
// (0012), and "clean the logs" means READABLE, never fewer rows (intake
// correction A-2). Everything here is presentation.
//
// ⚠ A MISSING ACTION MUST STILL READ. New actions are added by every migration
// that logs one, and a lookup table alone would print a raw code the day it
// shipped. `describeAuditAction` falls back to tidying the code itself —
// `cash_remittance.handed_over` → "Cash remittance · handed over" — so the
// worst case is plain English in the wrong register, not `kyc_decision`.

/** What kind of thing an action concerns — the row's group, for filtering. */
export type AuditGroup =
  | "money"
  | "people"
  | "bookings"
  | "sessions"
  | "safety"
  | "settings"
  | "other";

export interface AuditDescription {
  /** One sentence, sentence case, no identifiers. */
  label: string;
  group: AuditGroup;
}

/**
 * The actions actually written today, read off the deployed log. Anything not
 * here is tidied by the fallback below rather than guessed at.
 */
const ACTIONS: Record<string, AuditDescription> = {
  // ── money ────────────────────────────────────────────────────────────────
  "payroll.run": { label: "Ran payroll", group: "money" },
  "payroll.approve": { label: "Approved a payroll run", group: "money" },
  "payroll.mark_paid": { label: "Marked a payroll run paid", group: "money" },
  "payout.request": { label: "Requested a payout", group: "money" },
  "payout.settle": { label: "Settled a payout", group: "money" },
  "cash_remittance.instructed": {
    label: "Told a therapist where to hand cash over",
    group: "money",
  },
  "payment.dismissed_as_test": {
    label: "Dismissed a paid-but-uncredited payment as test data",
    group: "money",
  },
  "cash_remittance.received": { label: "Took cash from a therapist", group: "money" },
  "cash_remittance.hub_received": { label: "Hub received cash", group: "money" },
  "cash_remittance.collected": { label: "Main office collected cash", group: "money" },
  "cash_remittance.handed_over": { label: "Therapist handed cash over", group: "money" },
  "cash_remittance.rider_confirmed": { label: "Rider confirmed taking cash", group: "money" },
  "cash_remittance.rider_eligibility_checked": {
    label: "Checked whether a rider may carry cash",
    group: "money",
  },
  "cash_remittance.issue_reported": {
    label: "Reported a problem with a cash handover",
    group: "money",
  },
  "cash_remittance.manual_hub_receipt_correction": {
    label: "Corrected a hub cash receipt by hand",
    group: "money",
  },
  "cash_remittance.repair_removed_legacy": { label: "Repaired an old cash record", group: "money" },
  "cash_handling.split": { label: "Split cash between handovers", group: "money" },
  "cash_handoff.opened": { label: "Opened a cash handover", group: "money" },
  "cash_handoff.paid": { label: "Paid a cash handover", group: "money" },
  "cash_handoff.collected": { label: "Collected a cash handover", group: "money" },
  "client.cash_removed": { label: "Stopped a client paying in cash", group: "money" },
  "client.cash_restored": { label: "Let a client pay in cash again", group: "money" },
  "giftpass.batch_created": { label: "Created a GiftPass batch", group: "money" },

  // ── people ───────────────────────────────────────────────────────────────
  "staff.invite": { label: "Invited a new staff member", group: "people" },
  "staff.photo_changed": { label: "Changed a staff photo", group: "people" },
  rider_rank_change: { label: "Changed a rider's rank", group: "people" },
  therapist_rank_change: { label: "Changed a therapist's rank", group: "people" },
  // 0329 — the monthly system's own rank writes, and the hand-back of a
  // manually-set one. Same "people" group as the manual changes they mirror.
  "tier_rank.monthly_apply": {
    label: "Monthly tier ranking set a staff rank",
    group: "people",
  },
  "tier_rank.release_to_system": {
    label: "Handed a staff rank back to the monthly system",
    group: "people",
  },
  role_change: { label: "Changed a staff member's role", group: "people" },
  account_creation: { label: "Created an account", group: "people" },
  force_logout: { label: "Forced a sign-out", group: "people" },
  "qualification.grant": { label: "Allowed a therapist to perform a service", group: "people" },
  "qualification.revoke": { label: "Stopped a therapist performing a service", group: "people" },
  "staff_home_location.submit": {
    label: "Staff member submitted their home area",
    group: "people",
  },
  "staff_home_location.approve": { label: "Approved a staff member's home area", group: "people" },
  "staff_home_location.reject": { label: "Rejected a staff member's home area", group: "people" },
  kyc_submitted: { label: "A client submitted their ID", group: "people" },
  kyc_decision: { label: "Decided on a client's ID", group: "people" },
  kyc_document_viewed: { label: "Opened a client's ID document", group: "people" },
  "schedule.publish": { label: "Published a duty schedule", group: "people" },

  // ── bookings and dispatch ────────────────────────────────────────────────
  "booking.cancelled": { label: "Cancelled a booking", group: "bookings" },
  "booking.cancelled_mid_service": {
    label: "Cancelled a booking during the service",
    group: "bookings",
  },
  "booking.in_service_change": {
    label: "Changed a service while it was running",
    group: "bookings",
  },
  "dispatch.assign_specific_rider": { label: "Assigned a particular rider", group: "bookings" },
  "dispatch.release_rider": { label: "Released a rider from a job", group: "bookings" },
  "therapist_dispatch.rider_assigned": {
    label: "Assigned a rider to collect a therapist",
    group: "bookings",
  },
  "therapist_dispatch.return_destination_reresolved": {
    label: "Re-resolved where a therapist is going back to",
    group: "bookings",
  },
  "therapist_dispatch.cancelled_stranded": {
    label: "Cancelled a pick-up and left a therapist waiting",
    group: "bookings",
  },

  // ── sessions ─────────────────────────────────────────────────────────────
  rider_session_generate: { label: "Generated rider sessions", group: "sessions" },
  rider_session_reassign: { label: "Reassigned a rider session", group: "sessions" },
  rider_session_retime: { label: "Changed a rider session's times", group: "sessions" },
  rider_session_start_on_behalf: {
    label: "Started a rider session for someone",
    group: "sessions",
  },
  rider_session_general_access: {
    label: "Opened general access to rider sessions",
    group: "sessions",
  },
  rider_session_trade_request: { label: "Asked to swap a rider session", group: "sessions" },
  rider_session_trade_withdraw: { label: "Withdrew a rider session swap", group: "sessions" },
  rider_session_trade_expire: { label: "A rider session swap expired", group: "sessions" },
  therapist_session_retime: { label: "Changed a therapist session's times", group: "sessions" },
  therapist_session_state_override: {
    label: "Overrode a therapist session's state",
    group: "sessions",
  },
  therapist_session_days_off: { label: "Set a therapist's days off", group: "sessions" },
  therapist_approval_approve: {
    label: "Approved a therapist's session request",
    group: "sessions",
  },
  "therapist_cycle.create": { label: "Created a therapist session cycle", group: "sessions" },
  therapist_pickup_range_override: {
    label: "Overrode a therapist pick-up range",
    group: "sessions",
  },

  // ── safety and support ───────────────────────────────────────────────────
  "therapist.sos_raised": { label: "A therapist raised an SOS", group: "safety" },
  "therapist.sos_acknowledged": { label: "Acknowledged an SOS", group: "safety" },
  "therapist.sos_resolved": { label: "Resolved an SOS", group: "safety" },
  "support_thread.escalated": { label: "Escalated a support conversation", group: "safety" },
  "support_thread.withdrawn": { label: "Withdrew a support conversation", group: "safety" },
  "support_thread.archived": { label: "Archived a support conversation", group: "safety" },
  "support_thread.restored": { label: "Restored a support conversation", group: "safety" },
  "support_thread.flagged_urgent": { label: "Flagged a support ticket urgent", group: "safety" },
  "support_thread.unflagged_urgent": {
    label: "Marked a support ticket not urgent",
    group: "safety",
  },
  "support_thread.urgency_cleared": {
    label: "Returned a support ticket to automatic urgency",
    group: "safety",
  },
  "superadmin.map.client_pins_read": {
    label: "Looked up client locations on the map",
    group: "safety",
  },
  "superadmin.map.active_booking_client_pins_read": {
    label: "Looked up which client each live booking belongs to",
    group: "safety",
  },

  // ── settings ─────────────────────────────────────────────────────────────
  mileage_rate_change: { label: "Changed the mileage rate", group: "settings" },
  giftpass_invalidate: { label: "Invalidated a GiftPass", group: "settings" },
};

/** `INSERT operating_settings` and friends — a bare SQL verb plus a table. */
const SQL_VERBS: Record<string, string> = {
  INSERT: "Added a row to",
  UPDATE: "Changed",
  DELETE: "Removed a row from",
};

function humanizeTable(name: string): string {
  return name.replaceAll("_", " ");
}

/** Sentence-cases a code: `cash_remittance.handed_over` → "Cash remittance · handed over". */
function tidyCode(action: string): string {
  const parts = action.split(".").map((part) => part.replaceAll("_", " ").trim());
  const [head, ...rest] = parts;
  const sentence = [head ? head.charAt(0).toUpperCase() + head.slice(1) : action, ...rest];
  return sentence.join(" · ");
}

export function describeAuditAction(action: string): AuditDescription {
  const known = ACTIONS[action];
  if (known) return known;

  // A trigger-written row: "UPDATE distance_surcharge_bands".
  const [verb, table] = action.split(" ");
  if (verb && table && SQL_VERBS[verb]) {
    return { label: `${SQL_VERBS[verb]} ${humanizeTable(table)}`, group: "settings" };
  }
  return { label: tidyCode(action), group: "other" };
}

/** `bookings:7f3e…` → the table and the id, for the Record column. */
export function parseAuditEntity(entity: string): { table: string; id: string | null } {
  const index = entity.indexOf(":");
  if (index === -1) return { table: entity, id: null };
  return { table: entity.slice(0, index), id: entity.slice(index + 1) || null };
}

/** Keys whose value is noise in a diff, not a fact an operator reads. */
const SKIPPED_KEYS = new Set(["id", "created_at", "updated_at", "client_request_id"]);

export interface AuditChange {
  field: string;
  from: string;
  to: string;
}

function show(value: unknown): string {
  if (value === null || value === undefined) return "—";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  return JSON.stringify(value);
}

/**
 * Field-level "from → to", so a row reads as a change rather than two blobs of
 * JSON. A row with only `after` (most of them) lists what was recorded.
 */
export function auditChanges(before: unknown, after: unknown): AuditChange[] {
  const b = (before ?? {}) as Record<string, unknown>;
  const a = (after ?? {}) as Record<string, unknown>;
  if (typeof b !== "object" || typeof a !== "object") return [];

  const fields = [...new Set([...Object.keys(b), ...Object.keys(a)])].filter(
    (key) => !SKIPPED_KEYS.has(key),
  );
  return fields
    .filter((field) => show(b[field]) !== show(a[field]))
    .map((field) => ({
      field: humanizeTable(field),
      from: show(b[field]),
      to: show(a[field]),
    }));
}
